import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { IPatientBaseline } from "../../@types/Baselines";
import {
  getLongFormattedDate,
  getShortFormattedDate,
} from "../../utils/dateFormatter";
import {
  StyledBaselineTooltipBox,
  StyledBaselineHeader,
  StyledBaselineDate,
  StyledBaselineValues,
  StyledBaselineValueRow,
} from "./BaselineTooltipOverlay.styles";

export interface IBaselineSegment {
  baseline: IPatientBaseline;
  xStart: number;
  xEnd: number;
  yValue: number;
}

interface IHoverState {
  segment: IBaselineSegment;
  clientX: number;
  clientY: number;
}

interface IBaselineTooltipOverlayProps {
  baselineSegments: IBaselineSegment[];
  xAxisMap?: Record<string, any>;
  yAxisMap?: Record<string, any>;
  offset?: { top: number; left: number; width: number; height: number };
  width?: number;
}

const TOOLTIP_OFFSET_Y = 12;
const HIT_AREA_STROKE_WIDTH = 10;

export function BaselineTooltipOverlay({
  baselineSegments,
  xAxisMap,
  yAxisMap,
  offset,
}: IBaselineTooltipOverlayProps) {
  const { t } = useTranslation();
  const [hoverState, setHoverState] = useState<IHoverState | null>(null);

  const xAxis = xAxisMap ? Object.values(xAxisMap)[0] : null;
  const yAxis = yAxisMap ? Object.values(yAxisMap)[0] : null;

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<SVGLineElement>, segment: IBaselineSegment) => {
      setHoverState({
        segment,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setHoverState(null);
  }, []);

  if (!xAxis?.scale || !yAxis?.scale || !offset) return null;

  const xScale = xAxis.scale;
  const yScale = yAxis.scale;

  const plotLeft = offset.left;
  const plotRight = offset.left + offset.width;

  const hasISHLTValues = (seg: IBaselineSegment) => {
    const { lowerCi, upperCi, firstTestTime, lastTestTime } = seg.baseline;
    return (
      seg.baseline.isISHLTBaseline &&
      lowerCi != null &&
      upperCi != null &&
      firstTestTime != null &&
      lastTestTime != null
    );
  };

  const renderTooltip = () => {
    if (!hoverState) return null;

    const { segment, clientX, clientY } = hoverState;
    const { baseline } = segment;
    const isISHLT = baseline.isISHLTBaseline;
    const showValues = hasISHLTValues(segment);

    const dateText = getLongFormattedDate(baseline.created);

    return createPortal(
      <div
        style={{
          position: "fixed",
          top: clientY + TOOLTIP_OFFSET_Y,
          left: clientX,
          transform: "translateX(-50%)",
          zIndex: 9999,
          pointerEvents: "none",
        }}
      >
        <StyledBaselineTooltipBox>
          <StyledBaselineHeader>
            <span>
              {t(
                isISHLT
                  ? "chart_baselineTooltip_ishlt"
                  : "chart_baselineTooltip_baseline"
              )}
            </span>
            <span>{baseline.baseline.toFixed(2)}</span>
          </StyledBaselineHeader>
          <StyledBaselineDate hasValues={showValues}>
            <span>{dateText || "-"}</span>
          </StyledBaselineDate>
          {showValues && (
            <StyledBaselineValues>
              <span>{t("chart_baselineTooltip_valuesUsed")}</span>
              <StyledBaselineValueRow>
                <span>{baseline.upperCi?.toFixed(2)}</span>
                <span>– {getShortFormattedDate(baseline.lastTestTime!)}</span>
              </StyledBaselineValueRow>
              <StyledBaselineValueRow>
                <span>{baseline.lowerCi?.toFixed(2)}</span>
                <span>– {getShortFormattedDate(baseline.firstTestTime!)}</span>
              </StyledBaselineValueRow>
            </StyledBaselineValues>
          )}
        </StyledBaselineTooltipBox>
      </div>,
      document.body
    );
  };

  return (
    <g className="baseline-tooltip-overlay">
      {baselineSegments.map((seg) => {
        const x1Px = xScale(seg.xStart);
        const x2Px = xScale(seg.xEnd);
        const yPx = yScale(seg.yValue);

        const clampedX1 = Math.max(x1Px, plotLeft);
        const clampedX2 = Math.min(x2Px, plotRight);

        if (clampedX1 >= clampedX2) return null;

        return (
          <line
            key={`baseline-hit-${seg.baseline.id}`}
            x1={clampedX1}
            y1={yPx}
            x2={clampedX2}
            y2={yPx}
            stroke="transparent"
            strokeWidth={HIT_AREA_STROKE_WIDTH}
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => handleMouseEnter(e, seg)}
            onMouseLeave={handleMouseLeave}
          />
        );
      })}
      {renderTooltip()}
    </g>
  );
}
